<template>
  <div class="">
    <!-- Document Viewer Modal -->
    <BaseDocumentViewer
      v-model:visible="viewerVisible"
      :blobData="viewerBlobData"
      :fileName="selectedDocument?.Name || 'Document'"
      :fileExtension="selectedDocument?.Extension || ''"
      :loading="actionLoading"
    />

    <div class="main" v-if="isLoading || (actionLoading && !viewerVisible)">
      <div class="ring"></div>
    </div>
    <div class="col-xl-12 col-lg-12 col-md-12 col-12">
      <div class="col-md-12 contentArea" style="position: absolute">
        <div v-if="documentsData" class="pb-4 pcTable" style="overflow-y: auto">
          <DataTable
            :value="documentsData"
            stripedRows
            v-model:filters="filters"
            filterDisplay="row"
            scrollable
            size="small"
            selectionMode="single"
            showGridlines
            paginator
            :rows="20"
          >
            <Column field="Name" header="Document Name">
              <template #body="{ data }">
                {{ data.Name }}
              </template>
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-model="filterModel.value"
                  type="text"
                  @input="filterCallback()"
                  class="p-column-filter"
                />
              </template>
            </Column>

            <Column field="Modified" header="Modified">
              <template #body="{ data }">
                {{ formatGridDate(data.Modified) }}
              </template>
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-model="filterModel.value"
                  type="text"
                  @input="filterCallback()"
                  class="p-column-filter"
                />
              </template>
            </Column>

            <Column field="UploadedOn" header="Uploaded On">
              <template #body="{ data }">
                {{ formatGridDate(data.UploadedOn) }}
              </template>
              <template #filter="{ filterModel, filterCallback }">
                <InputText
                  v-model="filterModel.value"
                  type="text"
                  @input="filterCallback()"
                  class="p-column-filter"
                />
              </template>
            </Column>

            <Column header="Actions">
              <template #body="{ data }">
                <div style="display: flex; justify-content: center; gap: 10px;">
                  <div 
                    :title="isFormatSupported(data) ? 'view' : 'Format not supported'" 
                    :class="['action-btn', 'action-btn-view', { 'action-btn-disabled': !isFormatSupported(data) }]" 
                    @click="isFormatSupported(data) && handleView(data)"
                  ><i class="fas fa-eye"></i></div>
                  <div 
                    :title="isFormatSupported(data) ? 'download' : 'Format not supported'" 
                    :class="['action-btn', 'action-btn-download', { 'action-btn-disabled': !isFormatSupported(data) }]" 
                    @click="isFormatSupported(data) && handleDownload(data)"
                  ><i class="fas fa-download"></i></div>
                </div>
              </template>
            </Column>
          </DataTable>
        </div>

        <div v-if="documentsData" class="mobileTable">
          <div
            v-for="item in documentsData"
            :key="documentsData.indexOf(item)"
            class="mobilScreenTable"
          >
            <div style="display: flex">
              <div class="infoBoxText">
                <a style="font-weight: 500; font-size: 15px">Document Name</a>
                <a style="font-weight: 500; font-size: 13px">{{ item.Name }}</a>
              </div>
            </div>
            <hr class="mobileScreenHrStyle" />

            <div class="mobileScreenChild">
              <div>
                <a class="mobileScreenText">Modified Date</a>
              </div>
              <div>
                <a class="mobileScreenText">{{
                  formatGridDate(item.Modified)
                }}</a>
              </div>
            </div>

            <div class="mobileScreenChild">
              <div>
                <a class="mobileScreenText">Uploaded On</a>
              </div>
              <div>
                <a class="mobileScreenText">{{
                  formatGridDate(item.UploadedOn)
                }}</a>
              </div>
            </div>

            <hr class="mobileScreenHrStyle" />

            
            <div class="mobileScreenChild">
              <div>
                Actions
              </div>
              <div style="display: flex; justify-content: center; gap: 10px; padding-top: 5px;">
                <div 
                  :title="isFormatSupported(item) ? 'view' : 'Format not supported'" 
                  :class="['action-btn', 'action-btn-view', { 'action-btn-disabled': !isFormatSupported(item) }]" 
                  @click="isFormatSupported(item) && handleView(item)"
                ><i class="fas fa-eye"></i></div>
                <div 
                  :title="isFormatSupported(item) ? 'download' : 'Format not supported'" 
                  :class="['action-btn', 'action-btn-download', { 'action-btn-disabled': !isFormatSupported(item) }]" 
                  @click="isFormatSupported(item) && handleDownload(item)"
                ><i class="fas fa-download"></i></div>
              </div>
            </div>
          </div>
        </div>

        <section
          v-if="documentsData && documentsData.length == 0"
          class="section"
          style="text-align: center"
        >
          <div class="content has-text-grey has-text-centered mb-4">
            <p class="noMargin">
              <i
                style="font-size: 24px; color: #939393"
                class="ri-question-line"
              ></i>
            </p>
            <p class="noMargin">Nothing here.</p>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import types from "./types";
import deepCopy from "../helpers/deepCopy";
import { FilterMatchMode } from "primevue/api";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import BaseDocumentViewer from "@/components/Base/BaseDocumentViewer.vue";

const MIME_TYPES = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  csv: 'text/csv',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xls: 'application/vnd.ms-excel',
};

// File extensions that support view and download in the document viewer
const SUPPORTED_FORMATS = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'csv', 'docx', 'xlsx', 'xls'];

export default {
  name: "DocumentsScreen",
  components: { DataTable, Column, BaseDocumentViewer },
  props: {},
  data() {
    return {
      documentsData: null,
      isLoading: false,
      actionLoading: false,
      viewerVisible: false,
      viewerBlobData: null,
      selectedDocument: null,
      filters: {
        Name: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
        Modified: { value: null, matchMode: FilterMatchMode.CONTAINS },
        UploadedOn: { value: null, matchMode: FilterMatchMode.CONTAINS },
      },
    };
  },

  async mounted() {
    this.isLoading = true;
    let err, result;
    [err, result] = await this.$store.dispatch(types.GET_DOCUMENTS);
    if (result && result.Data) {
      this.documentsData = deepCopy(result.Data).map((item) => ({
        ...item,
        Modified: this.formatGridDate(item.Modified),
        UploadedOn: this.formatGridDate(item.UploadedOn),
      }));
    }
    this.isLoading = false;
  },
  computed: {},
  methods: {
    formatGridDate(date) {
      return date == null || date == ""
        ? ""
        : new Date(date).toLocaleDateString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "numeric",
            minute: "2-digit",
          });
    },

    getFileExtension(fileName) {
      return fileName?.split('.').pop()?.toLowerCase() || '';
    },

    getMimeType(fileName) {
      // Fallback: extract extension from fileName
      const ext = this.getFileExtension(fileName);
      return MIME_TYPES[ext] || 'application/octet-stream';
    },

    isFormatSupported(document) {
      // Check if document format is supported for view/download
      const ext = document.Extension?.toLowerCase()?.replace('.', '') || this.getFileExtension(document.Name);
      return SUPPORTED_FORMATS.includes(ext);
    },

    async handleView(document) {
      // Open modal immediately and show loading inside it
      this.selectedDocument = document;
      this.viewerBlobData = null;
      this.viewerVisible = true;
      this.actionLoading = true;

      try {
        const [err, result] = await this.$store.dispatch(types.DOWNLOAD_FILE, {
          FileName: document.Name,
        });

        if (err || !result) {
          this.viewerVisible = false;
          this.$swal('Error!', 'Failed to load document. Please try again.', 'error');
          return;
        }

        // result.data contains the ArrayBuffer from axios response
        this.viewerBlobData = result.data || result;
      } catch (error) {
        console.error('Error viewing document:', error);
        this.viewerVisible = false;
        this.$swal('Error!', 'Failed to load document. Please try again.', 'error');
      } finally {
        this.actionLoading = false;
      }
    },

    async handleDownload(document) {
      this.actionLoading = true;
      try {
        const [err, result] = await this.$store.dispatch(types.DOWNLOAD_FILE, {
          FileName: document.Name,
        });

        if (err || !result) {
          this.$swal('Error!', 'Failed to download document. Please try again.', 'error');
          return;
        }

        // result.data contains the ArrayBuffer from axios response
        const blobData = result.data || result;
        
        // Create blob and trigger download
        const ext = document.Extension?.toLowerCase()?.replace('.', '');
        const mimeType = ext ? (MIME_TYPES[ext] || 'application/octet-stream') : this.getMimeType(document.Name);
        
        const blob = new Blob([blobData], { type: mimeType });
        const url = URL.createObjectURL(blob);
        
        const link = window.document.createElement('a');
        link.href = url;
        link.download = document.Name;
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
        
        // Cleanup
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading document:', error);
        this.$swal('Error!', 'Failed to download document. Please try again.', 'error');
      } finally {
        this.actionLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.main {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 9998;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ring {
  position: absolute;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  animation: ring 2s linear infinite;
}
@keyframes ring {
  0% {
    transform: rotate(0deg);
    box-shadow: 1px 5px 2px #4fa8eb;
  }
  50% {
    transform: rotate(180deg);
    box-shadow: 1px 5px 2px #4fa8eb;
  }
  100% {
    transform: rotate(360deg);
    box-shadow: 1px 5px 2px #0d88e6;
  }
}
.ring:before {
  position: absolute;
  content: "";
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  border-radius: 50%;
  box-shadow: 0 0 5px rgba(255, 255, 255, 0.3);
}

.datagrid-table {
  border: 1px solid #e4e4e4;
  border-collapse: collapse;
  border-radius: 8px;
  thead {
    background-color: #ffffff;
    color: #2a404c;

    th {
      padding: 15px;
      border-top-color: #ffffff;
      border-bottom: 1px solid #f3eded;
      font-size: 14px;
      font-weight: 600;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.5;
      letter-spacing: normal;
    }
  }
  tbody {
    tr {
      font-size: 13px;
      color: #2a404c;
      &:nth-child(even) {
        background-color: #fafafa;
      }
    }
    td {
      padding: 11px 15px;
    }
    tr:hover,
    tr:active {
      background-color: #3bb0da3a;
    }
  }
  .infoBoxText {
    display: flex;
    flex-direction: column;
    margin-top: 20px;
    margin-left: 15px;
    font-family: "Open Sans", sans-serif;
  }
}
// Mobile Screen Table Css
.mobilScreenTable {
  height: 100%;
  width: 100%;
  background-color: white;
  border-radius: 15px;
  box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 12px;
  margin-bottom: 15px;
  padding-bottom: 15px;
}

.mobileScreenHrStyle {
  margin-top: 3px;
  margin-bottom: 3px;
}

.mobileScreenChild {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 16px;
  padding-left: 16px;
}

.mobileScreenText {
  font-weight: 500;
  font-size: 13px;
}

@media only screen and (min-width: 768px) {
  .mobileTable {
    display: none;
  }
  .contentArea {
    padding-top: 70px;
  }
}
@media only screen and (max-width: 768px) {
  .pcTable {
    display: none;
  }
  .contentArea {
    padding-top: 55px;
    padding-left: 0px !important;
  }
}

.action-btn {
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.action-btn-view {
  border: 1px solid #6adf9d;
  color: #6adf9d;
}

.action-btn-view:hover {
  background-color: #6adf9d;
  color: white;
}

.action-btn-download {
  border: 1px solid #80d7ee;
  color: #80d7ee;
}

.action-btn-download:hover {
  background-color: #80d7ee;
  color: white;
}

.action-btn-disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.action-btn-disabled:hover {
  background-color: unset;
  color: #6adf9d;
}
</style>
